import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { Select, Switch, TextArea, TextInput } from '../../../components/Field';
import { Modal } from '../../../overlay';
import { toast } from '../../../components/Toast';
import { api, errorMessage, ApiRequestError } from '../../../lib/api';
import {
  TELEMETRY_RATE_MAX_HZ,
  TELEMETRY_RATE_MIN_HZ,
  STALE_AFTER_MAX_MS,
  STALE_AFTER_MIN_MS,
  resolveThresholds,
} from '@shared/defaults';
import { validateDroneInput } from '@shared/validate';
import type { Drone, DroneCredentialMode, DroneGroup, DroneProtocol } from '@shared/types';

export interface DroneFormResult {
  drone: Drone;
  generatedCredential?: string;
}

interface DroneFormModalProps {
  open: boolean;
  /** 为空表示新建 */
  drone: Drone | null;
  groups: readonly DroneGroup[];
  defaultGroupId?: string | null;
  onClose: () => void;
  onSaved: (result: DroneFormResult) => void;
}

interface FormState {
  name: string;
  uniqueId: string;
  model: string;
  vendor: string;
  protocol: DroneProtocol;
  host: string;
  port: string;
  basePath: string;
  wsPath: string;
  credentialMode: DroneCredentialMode;
  credential: string;
  adapterProfile: string;
  fieldMapText: string;
  groupId: string;
  tagsText: string;
  enabled: boolean;
  telemetryRateHz: string;
  staleAfterMs: string;
  thresholdsText: string;
  notes: string;
}

const DEFAULT_FORM: FormState = {
  name: '',
  uniqueId: '',
  model: '',
  vendor: '',
  protocol: 'http',
  host: '127.0.0.1',
  port: '9201',
  basePath: '/api/v1',
  wsPath: '/api/v1/telemetry/stream',
  credentialMode: 'apiKey',
  credential: '',
  adapterProfile: 'canonical',
  fieldMapText: '',
  groupId: '',
  tagsText: '',
  enabled: true,
  telemetryRateHz: '10',
  staleAfterMs: '3000',
  thresholdsText: '',
  notes: '',
};

function toForm(drone: Drone): FormState {
  return {
    name: drone.name,
    uniqueId: drone.uniqueId,
    model: drone.model,
    vendor: drone.vendor,
    protocol: drone.endpoint.protocol,
    host: drone.endpoint.host,
    port: String(drone.endpoint.port),
    basePath: drone.endpoint.basePath,
    wsPath: drone.endpoint.wsPath,
    credentialMode: drone.credentialMode,
    credential: '',
    adapterProfile: drone.adapterProfile ?? '',
    fieldMapText: drone.fieldMap ? JSON.stringify(drone.fieldMap, null, 2) : '',
    groupId: drone.groupId ?? '',
    tagsText: drone.tags.join(', '),
    enabled: drone.enabled,
    telemetryRateHz: String(drone.telemetryRateHz),
    staleAfterMs: String(drone.staleAfterMs),
    thresholdsText:
      Object.keys(drone.thresholds ?? {}).length > 0 ? JSON.stringify(drone.thresholds, null, 2) : '',
    notes: drone.notes ?? '',
  };
}

/**
 * 无人机注册/编辑表单。
 *
 * 这里使用「功能型弹窗」，因此在默认作用域下会居中并带模糊背景；
 * 在单机管理（hard-realtime）作用域下会自动降级为内嵌面板。
 */
export function DroneFormModal({
  open,
  drone,
  groups,
  defaultGroupId,
  onClose,
  onSaved,
}: DroneFormModalProps) {
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const isEdit = drone !== null;

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(drone ? toForm(drone) : { ...DEFAULT_FORM, groupId: defaultGroupId ?? '' });
  }, [open, drone, defaultGroupId]);

  const defaultThresholds = useMemo(() => resolveThresholds({}), []);

  function patch<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function parseJsonField(
    text: string,
    field: string,
    label: string,
  ): { value: unknown; error?: string } {
    if (text.trim() === '') return { value: undefined };
    try {
      const parsed = JSON.parse(text) as unknown;
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { value: undefined, error: `${label}需要是一个 JSON 对象` };
      }
      return { value: parsed };
    } catch {
      return { value: undefined, error: `${label}不是合法的 JSON（${field}）` };
    }
  }

  async function submit() {
    const fieldMap = parseJsonField(form.fieldMapText, 'fieldMap', '字段映射');
    const thresholds = parseJsonField(form.thresholdsText, 'thresholds', '告警阈值');
    const localErrors: Record<string, string> = {};
    if (fieldMap.error) localErrors.fieldMap = fieldMap.error;
    if (thresholds.error) localErrors.thresholds = thresholds.error;

    const tags = form.tagsText
      .split(/[,，\s]+/)
      .map((tag) => tag.trim())
      .filter((tag) => tag !== '');

    const payload = {
      name: form.name.trim(),
      uniqueId: form.uniqueId.trim(),
      model: form.model.trim(),
      vendor: form.vendor.trim(),
      endpoint: {
        protocol: form.protocol,
        host: form.host.trim(),
        port: Number(form.port),
        basePath: form.basePath.trim(),
        wsPath: form.wsPath.trim(),
      },
      credentialMode: form.credentialMode,
      credential: form.credential.trim(),
      adapterProfile: form.adapterProfile.trim(),
      fieldMap: fieldMap.value,
      groupId: form.groupId === '' ? null : form.groupId,
      tags,
      enabled: form.enabled,
      telemetryRateHz: Number(form.telemetryRateHz),
      staleAfterMs: Number(form.staleAfterMs),
      thresholds: thresholds.value,
      notes: form.notes.trim(),
    };

    // 新建时凭据必填（none 模式除外），编辑时留空表示不修改
    const validation = validateDroneInput({
      ...payload,
      credential: isEdit ? '' : payload.credential,
    });
    const merged: Record<string, string> = { ...localErrors };
    for (const error of validation) {
      if (!merged[error.field]) merged[error.field] = error.message;
    }
    if (!isEdit && form.credentialMode !== 'none' && payload.credential.length < 8) {
      merged.credential = '新建时必须填写不少于 8 位的接入凭据';
    }
    setErrors(merged);
    if (Object.keys(merged).length > 0) return;

    setSaving(true);
    try {
      const body: Record<string, unknown> = { ...payload };
      delete body.fieldMap;
      delete body.adapterProfile;
      if (fieldMap.value !== undefined) body.fieldMap = fieldMap.value;
      if (form.adapterProfile.trim() !== '') body.adapterProfile = form.adapterProfile.trim();
      if (isEdit && payload.credential === '') delete body.credential;
      if (thresholds.value === undefined) delete body.thresholds;

      const saved = isEdit
        ? await api.patch<Drone>(`/api/drones/${drone.id}`, body)
        : await api.post<Drone>('/api/drones', body);

      // 凭据在编辑态是单独接口，避免和普通字段一起被覆盖
      if (isEdit && payload.credential !== '') {
        await api.post<Drone>(`/api/drones/${drone.id}/credential`, {
          credential: payload.credential,
          credentialMode: form.credentialMode,
        });
      }

      toast.ok(isEdit ? '无人机已更新' : '无人机已注册');
      onSaved({ drone: saved });
      onClose();
    } catch (error) {
      if (error instanceof ApiRequestError) {
        const mapped: Record<string, string> = {};
        for (const [field, message] of Object.entries(error.fieldErrors)) mapped[field] = message;
        setErrors(mapped);
        toast.critical(errorMessage(error));
      } else {
        toast.critical(errorMessage(error));
      }
    } finally {
      setSaving(false);
    }
  }

  const credentialHint =
    form.credentialMode === 'none'
      ? '无人机端不校验来源，任何客户端都可读取遥测，仅建议在隔离网络中使用。'
      : form.credentialMode === 'apiKey'
        ? '无人机只支持固定 API Key 时，浏览器会直接拿到明文凭据（依赖 HTTPS 保护）。'
        : '平台用共享密钥签发短期令牌，长期凭据不会下发到浏览器（推荐）。';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `编辑无人机 · ${drone?.name ?? ''}` : '注册新无人机'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            取消
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void submit()}>
            {isEdit ? '保存修改' : '完成注册'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <SectionTitle>基本信息</SectionTitle>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextInput
            label="名称"
            required
            value={form.name}
            error={errors.name}
            onChange={(event) => patch('name', event.target.value)}
            placeholder="例如：巡检 01 号"
          />
          <TextInput
            label="机身唯一 ID"
            required
            value={form.uniqueId}
            error={errors.uniqueId ?? errors['uniqueId']}
            hint="与无人机端上报的标识完全一致，仅允许字母、数字与 - _ . :"
            onChange={(event) => patch('uniqueId', event.target.value)}
            placeholder="例如：HAV-001"
          />
          <TextInput
            label="型号"
            required
            value={form.model}
            error={errors.model}
            onChange={(event) => patch('model', event.target.value)}
            placeholder="例如：Havorix X4"
          />
          <TextInput
            label="厂商"
            value={form.vendor}
            error={errors.vendor}
            onChange={(event) => patch('vendor', event.target.value)}
          />
        </div>

        <SectionTitle>接入地址（每台无人机一个独立服务）</SectionTitle>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label="协议"
            value={form.protocol}
            error={errors['endpoint.protocol']}
            options={[
              { value: 'http', label: 'http' },
              { value: 'https', label: 'https' },
            ]}
            onChange={(event) => patch('protocol', event.target.value as DroneProtocol)}
          />
          <TextInput
            label="域名 / IP"
            required
            value={form.host}
            error={errors['endpoint.host']}
            hint="不要带协议前缀或路径；生产环境除本机回环外必须使用 https"
            onChange={(event) => patch('host', event.target.value)}
          />
          <TextInput
            label="端口"
            required
            inputMode="numeric"
            value={form.port}
            error={errors['endpoint.port']}
            onChange={(event) => patch('port', event.target.value.replace(/[^\d]/g, ''))}
          />
          <TextInput
            label="REST 根路径"
            value={form.basePath}
            error={errors['endpoint.basePath']}
            onChange={(event) => patch('basePath', event.target.value)}
          />
          <TextInput
            label="WebSocket 路径"
            value={form.wsPath}
            error={errors['endpoint.wsPath']}
            hint="WebSocket 必须通过 ?token= 查询参数鉴权"
            onChange={(event) => patch('wsPath', event.target.value)}
          />
        </div>

        <SectionTitle>鉴权与适配</SectionTitle>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label="凭据模式"
            value={form.credentialMode}
            error={errors.credentialMode}
            hint={credentialHint}
            options={[
              { value: 'platformToken', label: 'platformToken（平台签发短期令牌）' },
              { value: 'apiKey', label: 'apiKey（无人机固定 API Key）' },
              { value: 'none', label: 'none（不鉴权）' },
            ]}
            onChange={(event) => patch('credentialMode', event.target.value as DroneCredentialMode)}
          />
          <TextInput
            label={isEdit ? '接入凭据（留空表示不修改）' : '接入凭据'}
            required={!isEdit && form.credentialMode !== 'none'}
            type="password"
            autoComplete="new-password"
            value={form.credential}
            error={errors.credential}
            disabled={form.credentialMode === 'none'}
            hint={isEdit ? '填写后立即轮换，至少 8 位' : '至少 8 位'}
            onChange={(event) => patch('credential', event.target.value)}
          />
          <TextInput
            label="适配器配置文件"
            value={form.adapterProfile}
            hint="canonical / typec / flat，留空按通用解析处理"
            onChange={(event) => patch('adapterProfile', event.target.value)}
          />
          <Select
            label="分组"
            value={form.groupId}
            placeholder="未分组"
            options={groups.map((group) => ({ value: group.id, label: group.name }))}
            onChange={(event) => patch('groupId', event.target.value)}
          />
        </div>

        <TextArea
          label="字段映射（JSON）"
          value={form.fieldMapText}
          error={errors.fieldMap}
          rows={3}
          hint='把厂商原始字段映射到规范字段，例如 {"battery.percent":"battery.remain_percent"}'
          onChange={(event) => patch('fieldMapText', event.target.value)}
        />

        <SectionTitle>运行参数</SectionTitle>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextInput
            label="遥测频率（Hz）"
            inputMode="decimal"
            value={form.telemetryRateHz}
            error={errors.telemetryRateHz}
            hint={`${TELEMETRY_RATE_MIN_HZ}–${TELEMETRY_RATE_MAX_HZ} Hz，作为轮询兜底频率`}
            onChange={(event) => patch('telemetryRateHz', event.target.value)}
          />
          <TextInput
            label="失联判定阈值（ms）"
            inputMode="numeric"
            value={form.staleAfterMs}
            error={errors.staleAfterMs}
            hint={`${STALE_AFTER_MIN_MS}–${STALE_AFTER_MAX_MS} ms，超过即判定为离线`}
            onChange={(event) => patch('staleAfterMs', event.target.value)}
          />
          <TextInput
            label="标签"
            value={form.tagsText}
            error={errors.tags}
            hint="用逗号分隔，最多 12 个"
            onChange={(event) => patch('tagsText', event.target.value)}
          />
          <div className="flex items-end pb-1">
            <Switch
              checked={form.enabled}
              label="启用该设备"
              hint="停用后不建立链路，也不能进入单机管理"
              onChange={(checked) => patch('enabled', checked)}
            />
          </div>
        </div>

        <TextArea
          label="告警阈值覆盖（JSON，留空用平台默认）"
          value={form.thresholdsText}
          error={errors.thresholds}
          rows={3}
          hint={`默认值：${JSON.stringify(defaultThresholds)}`}
          onChange={(event) => patch('thresholdsText', event.target.value)}
        />

        <TextArea
          label="备注"
          value={form.notes}
          error={errors.notes}
          rows={2}
          onChange={(event) => patch('notes', event.target.value)}
        />
      </div>
    </Modal>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <div className="mt-1 flex items-center gap-2 border-b border-line pb-1.5 text-xs font-semibold text-fg-muted">
      {children}
    </div>
  );
}
